import { useEffect, useState, useRef } from "react";
import {

  Sparkles,

} from "lucide-react";
import { Monitor, X, ChevronRight } from "lucide-react";

const PUZZLE_KEY = "blackout_puzzle_unlocked";
const SECRET_WORD = "CIPHER"; // ROT+3 → RULJDPL... wait, ROT-3: O→R, R→U, I→L, G→J, A→D, M→P, I→L → RULJDPL
// Encoded with Caesar +3: each letter +3 → displayed as RULJDPL
// User sees RULJDPL, applies -3, finds ORIGAMI
const ENCODED_WORD = "FLSKHU"; // ORIGAMI + 3
const PROMO_CODE = "DECODE10";

const GLITCH_CSS = `
@keyframes glitch-clip1 {
  0%,100% { clip-path: inset(0 0 90% 0); transform: translate(-3px, 1px); opacity:0; }
  8%       { clip-path: inset(5% 0 55% 0);  transform: translate(3px, 0);   opacity:1; }
  18%      { clip-path: inset(55% 0 5% 0);  transform: translate(-2px, 0);  opacity:1; }
  28%      { clip-path: inset(30% 0 40% 0); transform: translate(2px, -1px);opacity:1; }
  38%      { opacity: 0; }
}
@keyframes glitch-clip2 {
  0%,100% { clip-path: inset(40% 0 20% 0); transform: translate(3px, 0);  opacity:0; }
  12%     { clip-path: inset(15% 0 65% 0); transform: translate(-3px, 0); opacity:1; }
  22%     { clip-path: inset(65% 0 5% 0);  transform: translate(2px, 1px);opacity:1; }
  32%     { clip-path: inset(20% 0 50% 0); transform: translate(-1px, 0); opacity:1; }
  42%     { opacity: 0; }
}
@keyframes glitch-shake {
  0%,100% { transform: translate(0,0) skew(0); }
  10% { transform: translate(-2px, 1px) skew(-0.5deg); }
  20% { transform: translate(2px, -2px) skew(0.5deg); }
  30% { transform: translate(-2px, 0) skew(0); }
  40% { transform: translate(1px, 2px); }
  50% { transform: translate(-1px, -1px); }
  60% { transform: translate(0, 0); }
}
@keyframes glitch-color-flash {
  0%,100% { filter: none; }
  10% { filter: hue-rotate(90deg) brightness(1.5); }
  20% { filter: hue-rotate(-90deg) brightness(0.8); }
  30% { filter: none; }
}
.glitch-wrap { position: relative; display: inline-flex; }
.glitch-wrap .glitch-layer1,
.glitch-wrap .glitch-layer2 {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  opacity: 0; pointer-events: none;
}
.glitch-wrap.glitching {
  animation: glitch-shake 0.5s steps(1) 1, glitch-color-flash 0.5s steps(1) 1;
}
.glitch-wrap.glitching .glitch-layer1 {
  animation: glitch-clip1 0.5s steps(1) 1;
  color: #f97316;
}
.glitch-wrap.glitching .glitch-layer2 {
  animation: glitch-clip2 0.5s steps(1) 1;
  color: #818cf8;
}

.terminal-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.terminal-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.terminal-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(99,102,241,0.4);
  border-radius: 4px;
}
.terminal-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(99,102,241,0.7);
}

@keyframes terminal-in {
  from { opacity:0; transform: scale(0.94) translateY(12px); }
  to   { opacity:1; transform: scale(1) translateY(0); }
}
.terminal-modal { animation: terminal-in 0.28s cubic-bezier(0.16,1,0.3,1) forwards; }

@keyframes cursor-blink {
  0%,100% { opacity:1; } 50% { opacity:0; }
}
.cursor-blink { animation: cursor-blink 0.75s step-start infinite; }

@keyframes door-reveal {
  0%   { max-height: 0; opacity: 0; transform: scaleY(0.9); }
  40%  { opacity: 1; }
  100% { max-height: 500px; opacity: 1; transform: scaleY(1); }
}
.door-reveal {
  overflow: hidden;
  transform-origin: top center;
  animation: door-reveal 0.85s cubic-bezier(0.16,1,0.3,1) forwards;
}

@keyframes promo-glow {
  0%,100% { box-shadow: 0 0 20px rgba(99,102,241,0.3); }
  50%     { box-shadow: 0 0 50px rgba(99,102,241,0.6), 0 0 80px rgba(249,115,22,0.2); }
}
.promo-glow { animation: promo-glow 2s ease-in-out infinite; }

@keyframes scan-line-anim {
  0%   { top: 0; }
  100% { top: 100%; }
}
.scan-line {
  position: absolute; left: 0; right: 0; height: 2px; z-index: 1;
  background: linear-gradient(to right, transparent, rgba(99,102,241,0.5), transparent);
  animation: scan-line-anim 2.5s linear infinite;
  pointer-events: none;
}

@keyframes shake-err {
  0%,100% { transform: translateX(0); }
  20%     { transform: translateX(-4px); }
  40%     { transform: translateX(4px); }
  60%     { transform: translateX(-3px); }
  80%     { transform: translateX(3px); }
}
.shake-err { animation: shake-err 0.4s ease; }
`;

function injectCSS() {
  if (document.getElementById("glitch-styles-v3")) return;
  const s = document.createElement("style");
  s.id = "glitch-styles-v3";
  s.textContent = GLITCH_CSS;
  document.head.appendChild(s);
}

export function isPuzzleUnlocked() {
  try { return sessionStorage.getItem(PUZZLE_KEY) === "1"; } catch { return false; }
}
function unlockPuzzle() {
  try { sessionStorage.setItem(PUZZLE_KEY, "1"); } catch { }
}

// ── GlitchMonitor ──────────────────────────────────────────────────────
export function GlitchMonitor({ className = "", size = "h-6 w-6", onClick }) {
  const [glitching, setGlitching] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    injectCSS();
    const schedule = () => {
      const delay = 3000 + Math.random() * 4000;
      timerRef.current = setTimeout(() => {
        setGlitching(true);
        setTimeout(() => { setGlitching(false); schedule(); }, 600);
      }, delay);
    };
    const init = setTimeout(schedule, Math.random() * 2000);
    return () => { clearTimeout(init); clearTimeout(timerRef.current); };
  }, []);

  return (
    <span
      className={`glitch-wrap cursor-pointer select-none ${glitching ? "glitching" : ""} ${className}`}
      onClick={onClick}
    >
      <Monitor className={size} />
      <span className="glitch-layer1"><Monitor className={size} /></span>
      <span className="glitch-layer2"><Monitor className={size} /></span>
    </span>
  );
}

// ── Caesar table partielle ─────────────────────────────────────────────
// On montre 6 paires pour donner la logique sans tout révéler
const CAESAR_SAMPLE = [
  ["B", "E"],
  ["W", "Z"], ["E", "H"], ["P", "S"],
];

// ── Terminal lines ─────────────────────────────────────────────────────
const LINES = [
  { text: "BLACKOUT_OS v2.4.1 // CANAL SÉCURISÉ", delay: 0, color: "text-indigo-300" },
  { text: "Initialisation...", delay: 500, color: "text-slate-400" },
  { text: "ACCÈS ACCORDÉ — NIVEAU 1", delay: 1100, color: "text-emerald-400 font-bold" },
  { text: "─────────────────────────────────", delay: 1700, color: "text-slate-700" },
  { text: "TRANSMISSION INTERCEPTÉE", delay: 2300, color: "text-orange-400 font-bold" },
  { text: "Message chiffré détecté dans le réseau.", delay: 2900, color: "text-slate-300" },
  { text: "─────────────────────────────────", delay: 3500, color: "text-slate-700" },
  { text: "AUTHENTIFICATION REQUISE.", delay: 4200, color: "text-indigo-300 font-bold" },
];

// ── TerminalModal ──────────────────────────────────────────────────────
export function TerminalModal({ open, onClose }) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [showPuzzle, setShowPuzzle] = useState(false);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState(null); // null | "error" | "success"
  const [shaking, setShaking] = useState(false);
  const [alreadyUnlocked] = useState(isPuzzleUnlocked);
  const timersRef = useRef([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setVisibleCount(0); setShowPuzzle(false);
      setInput(""); setStatus(null); setShaking(false);
      return;
    }
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    if (alreadyUnlocked) {
      setVisibleCount(LINES.length);
      setShowPuzzle(true);
      setStatus("success");
      return;
    }

    LINES.forEach((line, i) => {
      const t = setTimeout(() => {
        setVisibleCount(i + 1);
        if (i === LINES.length - 1) setShowPuzzle(true);
      }, line.delay);
      timersRef.current.push(t);
    });
    return () => timersRef.current.forEach(clearTimeout);
  }, [open, alreadyUnlocked]);

  useEffect(() => {
    if (showPuzzle && !alreadyUnlocked && status !== "success") {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [showPuzzle, alreadyUnlocked, status]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim().toUpperCase() === SECRET_WORD) {
      unlockPuzzle();
      setStatus("success");
    } else {
      setShaking(true);
      setStatus("error");
      setTimeout(() => { setShaking(false); setStatus(null); setInput(""); }, 1000);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
      style={{ background: "rgba(2,4,16,0.90)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="terminal-modal relative w-full max-w-lg rounded-2xl terminal-scrollbar"
        style={{
          background: "#060b18",
          border: "1px solid rgba(99,102,241,0.4)",
          boxShadow: "0 0 80px rgba(99,102,241,0.15), 0 0 0 1px rgba(99,102,241,0.08)",
          maxHeight: "90vh",
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(99,102,241,0.4) transparent",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* <div className="scan-line" /> */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 1 }}>
          <div className="scan-line" />
        </div>


        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 sticky top-0 z-10" style={{ background: "rgba(6,11,24,0.95)" }}>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
          </div>
          <span className="text-[11px] font-mono text-slate-500 tracking-widest">BLACKOUT_OS // TERMINAL</span>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 font-mono text-sm space-y-1.5">
          {LINES.slice(0, visibleCount).map((line, i) => (
            <div key={i} className={`${line.color} leading-relaxed`}>
              <span className="text-indigo-600/40 mr-2 text-xs select-none">{String(i).padStart(2, "0")}</span>
              {line.text}
            </div>
          ))}
          {visibleCount < LINES.length && <span className="cursor-blink text-indigo-400 inline-block">█</span>}

          {/* Puzzle */}
          {showPuzzle && status !== "success" && (
            <div className="mt-4 pt-4 border-t border-white/5 space-y-5">

              {/* César bust + table */}
              <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl" style={{ background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)" }}>


                {/* Table */}
                <div className="flex-1 w-full">
                  <div className="grid grid-cols-4 gap-1 text-center">
                    {CAESAR_SAMPLE.map(([plain, enc]) => (
                      <div key={plain} className="col-span-1 flex flex-col items-center">
                        <span className="text-xs font-bold text-indigo-300">{plain}</span>
                        <span className="text-[10px] text-slate-600">↓</span>
                        <span className="text-xs font-bold text-orange-300">{enc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mot chiffré */}
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  {ENCODED_WORD.split("").map((char, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center justify-center w-9 h-10 rounded-lg text-lg font-black text-orange-300 font-mono"
                      style={{ background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)" }}
                    >
                      {char}
                    </span>
                  ))}
                </div>
              </div>

              {/* Input */}
              <form onSubmit={handleSubmit}>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${shaking ? "shake-err" : ""}`}
                  style={{ background: "rgba(99,102,241,0.08)", border: `1px solid ${status === "error" ? "rgba(239,68,68,0.5)" : "rgba(99,102,241,0.25)"}` }}>
                  <span className="text-indigo-500 select-none text-sm">$</span>
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value.toUpperCase())}
                    placeholder="Saisir le mot de passe"
                    maxLength={10}
                    className="flex-1 bg-transparent outline-none font-mono text-sm tracking-[0.2em] text-emerald-300 placeholder:text-slate-700"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button type="submit" className="text-indigo-400 hover:text-indigo-200 transition shrink-0">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                {status === "error" && (
                  <p className="mt-1.5 text-xs text-red-500 font-mono">ACCÈS REFUSÉ. Réessayez.</p>
                )}
              </form>
            </div>
          )}

          {/* Succès */}
          {status === "success" && (
            <div className="mt-4 pt-4 border-t border-emerald-500/20 space-y-2 font-mono">
              <p className="text-emerald-400 font-bold">✓ AUTHENTIFICATION RÉUSSIE</p>
              <p className="text-slate-400 text-sm">Accès niveau 2 déverrouillé.</p>
              <div className="mt-3 p-3 rounded-xl space-y-1.5" style={{ background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.2)" }}>
                <p className="text-slate-300 text-sm">→ Rendez-vous auprès du <em className="text-indigo-300 italic font-semibold">"Guide"</em></p>
                <p className="text-slate-300 text-sm flex items-center gap-2">
                  → Cherchez un <em className="text-indigo-300 italic font-semibold">"Conseil"</em><Sparkles className="h-4 w-4" />
                </p>
              </div>
              <p className="text-[11px] text-slate-600 mt-3">Appuyez sur ESC ou cliquez en dehors pour fermer.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PromoReveal ────────────────────────────────────────────────────────
const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

function DecryptText({ text, onDone }) {
  const [displayed, setDisplayed] = useState(() => "█".repeat(text.length));
  const [done, setDone] = useState(false);
  const frameRef = useRef(null);

  useEffect(() => {
    let iter = 0;
    const step = () => {
      setDisplayed(
        text.split("").map((char, i) => {
          if (i < iter) return char;
          if (char === " ") return " ";
          return CHARS[Math.floor(Math.random() * CHARS.length)];
        }).join("")
      );
      iter += 0.35;
      if (iter < text.length) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        setDisplayed(text);
        setDone(true);
        onDone?.();
      }
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [text, onDone]);

  return (
    <span className={`font-mono transition-colors ${done ? "text-emerald-300" : "text-orange-300"}`}>
      {displayed}
    </span>
  );
}

export function PromoReveal() {
  const [phase, setPhase] = useState(0); // 0=hidden 1=door 2=decrypt 3=done

  useEffect(() => {
    const handler = () => {
      if (phase > 0) return;
      setPhase(1);
      setTimeout(() => setPhase(2), 950);
    };
    window.addEventListener("blackout:reveal-promo", handler);
    return () => window.removeEventListener("blackout:reveal-promo", handler);
  }, [phase]);

  if (phase === 0) return null;

  return (
    <div className="door-reveal mt-4">
      <div
        className={`relative overflow-hidden rounded-2xl p-5 ${phase >= 3 ? "promo-glow" : ""}`}
        style={{
          background: "linear-gradient(135deg, #060b18 0%, #0d1428 100%)",
          border: "1px solid rgba(99,102,241,0.4)",
        }}
      >
        {phase >= 3 &&
          <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 1 }}>
            <div className="scan-line" />
          </div>
        }

        <div className="relative z-10 space-y-3 font-mono">
          <div className="text-[10px] text-indigo-400 tracking-[0.2em] uppercase">
            ◈ ACCÈS NIVEAU 2 — RÉCOMPENSE DÉBLOQUÉE
          </div>

          {phase >= 2 && (
            <>
              <div className="text-slate-400 text-sm">Code promo exclusif :</div>
              <div
                className="text-xl sm:text-2xl font-black py-3 px-4 rounded-xl text-center"
                style={{
                  background: "rgba(99,102,241,0.1)",
                  border: "1px solid rgba(99,102,241,0.25)",
                  letterSpacing: "0.15em",
                  wordBreak: "break-all",
                }}
              >
                <DecryptText
                  text={PROMO_CODE}
                  onDone={() => setPhase(3)}
                />
              </div>
              {phase >= 3 && (
                <div className="door-reveal space-y-1.5 text-sm">
                  <p className="text-emerald-400">✓ <span className="font-bold">−10%</span> sur votre premier achat</p>
                  <p className="text-slate-500 text-xs font-sans">À saisir lors de votre commande · Permanent · Non cumulable</p>
                  <p className="text-indigo-400 text-xs mt-2 font-sans">Vous avez trouvé ce qu'Origami avait caché. Bien joué. ✦</p>
                </div>
              )}
            </>
          )}

          {phase < 2 && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <span className="cursor-blink inline-block">█</span>
              <span>Déchiffrement en cours...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}